import React, { useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined';
import ViewCompactOutlinedIcon from '@mui/icons-material/ViewCompactOutlined';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { LensDisplay, LensSpec } from '~/types/leLens';

import { lensDisplayPreferences } from './lensDisplay';

type Preference = keyof LensDisplay;
type Props = {
  spec: LensSpec;
  disabled?: boolean;
  onChange: (changes: Partial<LensDisplay>) => Promise<void>;
};

export default function LensDisplaySettings({ spec, disabled = false, onChange }: Props) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const [optimistic, setOptimistic] = useState<Partial<LensDisplay> | null>(null);
  const display = { ...lensDisplayPreferences(spec), ...spec.display, ...optimistic };
  const hasNamedSection = Boolean(spec.sections?.some((section) => section.title.trim()));
  const change = async (changes: Partial<LensDisplay>) => {
    if (disabled || optimistic) return;
    setOptimistic(changes);
    try {
      await onChange(changes);
    } catch {
      // The shared mutation reports the error. Restore the saved switch state on failure.
    } finally {
      setOptimistic(null);
    }
  };
  const items = [
    {
      preference: 'compactLayout' as const,
      label: 'Compact layout',
      description: 'Keep period and filter controls in the menu.',
      checked: display.compactLayout !== false,
      icon: <ViewCompactOutlinedIcon fontSize="small" />,
    },
    {
      preference: 'showTitle' as const,
      label: 'Show title',
      description: 'Show the Lens name above the page.',
      checked: display.showTitle === true,
      icon: <TitleOutlinedIcon fontSize="small" />,
    },
  ];

  return (
    <>
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
        Display
      </Typography>
      <List disablePadding sx={{ mx: -1 }}>
        {items.map((item) => (
          <ListItem key={item.preference} sx={{ borderRadius: '8px', px: 1, py: 1.25 }}>
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 38 }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.description}
              primaryTypographyProps={{ fontSize: 15, fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: 13, id: `lens-${item.preference}-help`, lineHeight: 1.45 }}
            />
            <Switch
              checked={item.checked}
              disabled={disabled || Boolean(optimistic)}
              onChange={(_event, checked) => void change({ [item.preference]: checked })}
              size="small"
              slotProps={{ input: { 'aria-label': item.label, 'aria-describedby': `lens-${item.preference}-help` } }}
              sx={{ ml: 1 }}
            />
          </ListItem>
        ))}
      </List>
      {!spec.customView && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
            Colour treatment
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
            Choose where the Lens uses Ocean Teal and a touch of mint. Chart colours stay consistent.
          </Typography>
          <RadioGroup
            aria-label="Lens colour treatment"
            onChange={(_event, value) => void change({ colorTreatment: value as LensDisplay['colorTreatment'] })}
            value={display.colorTreatment ?? 'plain'}
          >
            {(
              [
                { value: 'plain', label: 'Light canvas', detail: 'The current beige page and white cards.' },
                { value: 'header', label: 'Teal title', detail: 'A stronger Lens identity at the top.' },
                { value: 'sections', label: 'Teal sections', detail: 'Distinct chapter headings for grouped cards.' },
                { value: 'metrics', label: 'Spotlight numbers', detail: 'Teal metric cards with mint figures.' },
              ] as const
            ).map((option) => (
              <Box
                key={option.value}
                component="label"
                sx={{
                  alignItems: 'center',
                  border: 1,
                  borderColor:
                    display.colorTreatment === option.value || (!display.colorTreatment && option.value === 'plain')
                      ? tokens.accent
                      : tokens.grid,
                  borderRadius: '8px',
                  cursor:
                    disabled || Boolean(optimistic) || (option.value === 'sections' && !hasNamedSection)
                      ? 'default'
                      : 'pointer',
                  display: 'flex',
                  gap: 1.25,
                  mb: 1,
                  p: 1,
                }}
              >
                <Radio
                  disabled={disabled || Boolean(optimistic) || (option.value === 'sections' && !hasNamedSection)}
                  size="small"
                  value={option.value}
                />
                <Box
                  aria-hidden
                  sx={{
                    bgcolor: tokens.canvas,
                    border: 1,
                    borderColor: tokens.grid,
                    borderRadius: '8px',
                    height: 36,
                    minWidth: 56,
                    overflow: 'hidden',
                    p: 0.5,
                  }}
                >
                  <Box sx={{ bgcolor: option.value === 'header' ? tokens.band : tokens.surface, height: 8, mb: 0.5 }} />
                  <Stack direction="row" spacing={0.5}>
                    <Box
                      sx={{
                        bgcolor: option.value === 'sections' || option.value === 'metrics' ? tokens.band : tokens.surface,
                        height: 15,
                        width: '38%',
                      }}
                    />
                    <Box sx={{ bgcolor: option.value === 'metrics' ? tokens.band : tokens.surface, height: 15, flex: 1 }} />
                  </Stack>
                </Box>
                <Box>
                  <Typography fontWeight={600} variant="body2">
                    {option.label}
                  </Typography>
                  <Typography color="text.secondary" variant="caption">
                    {option.value === 'sections' && !hasNamedSection
                      ? 'Add a section in Layout to use this.'
                      : option.detail}
                  </Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </Box>
      )}
    </>
  );
}
